import { supabase } from '../supabaseClient';

// Get current user
export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

// PROBLEMS
export const fetchProblems = async (sortOption = 'Sort: Newest') => {
  console.log('fetchProblems called with sortOption:', sortOption);

  let query = supabase
    .from('problems')
    .select('*, solutions (*), subtasks (*, assignee:users(name, is_active, inactive_user_name)), updater:users!fk_task_updater(name)');

  // --- CRITICAL SORTING LOGIC ---
  if (sortOption === 'Sort: Newest') {
    console.log('📅 Applying Newest sort: created_at DESC');
    query = query.order('created_at', { ascending: false }); // Newest first
  } else if (sortOption === 'Sort: Oldest') {
    console.log('📅 Applying Oldest sort: created_at ASC');
    query = query.order('created_at', { ascending: true }); // Oldest first
  } else if (sortOption === 'Sort: Recently Updated') {
    console.log('🔄 Applying Recently Updated sort: updated_at DESC');
    query = query.order('updated_at', { ascending: false }); // Latest update first
  } else if (sortOption === 'Sort: Title A-Z') {
    console.log('🔤 Applying Title A-Z sort: title ASC');
    // Use the 'title' column for alphabetical sort
    query = query.order('title', { ascending: true });
  } else if (sortOption === 'Sort: Title Z-A') {
    console.log('🔤 Applying Title Z-A sort: title DESC');
    // Use the 'title' column for reverse alphabetical sort
    query = query.order('title', { ascending: false });
  } else {
    console.log('📅 Applying default sort: created_at DESC');
    // Default fallback
    query = query.order('created_at', { ascending: false });
  }
  // --- END SORTING LOGIC ---

  const { data, error } = await query;

  if (error) {
    console.error('❌ Supabase query error:', error);
    throw error;
  }

  console.log('✅ Query successful, returned', data?.length || 0, 'problems');
  if (data && data.length > 0) {
    console.log('📊 First problem created_at:', data[0].created_at);
    console.log('📊 Last problem created_at:', data[data.length - 1].created_at);
  }

  // Fetch assigned users separately and enrich the data
  const assignedUserIds = [...new Set((data || []).map(p => p.assigned_to).filter(Boolean))];
  let assignedUsersMap = {};

  if (assignedUserIds.length > 0) {
    const { data: usersData, error: usersError } = await supabase
      .from('users')
      .select('id, name, email, is_active, inactive_user_name')
      .in('id', assignedUserIds);

    if (!usersError && usersData) {
      assignedUsersMap = usersData.reduce((acc, user) => {
        acc[user.id] = user;
        return acc;
      }, {});
      console.log('✅ Fetched', usersData.length, 'assigned users');
    } else if (usersError) {
      console.error('❌ Error fetching assigned users:', usersError);
    }
  }

  // Ensure solutions is always an array and enrich with assigned_user
  const problemsWithSolutions = (data || []).map(problem => ({
    ...problem,
    solutions: problem.solutions || [],
    subtasks: problem.subtasks || [],
    assigned_user: problem.assigned_to ? assignedUsersMap[problem.assigned_to] || null : null
  }));

  return problemsWithSolutions;
};

export const createProblem = async (problemData) => {
  // Get logged-in user
  const { data: { user } } = await supabase.auth.getUser();

  // Prepare data with user ID
  const newProblemData = {
    ...problemData,
    created_by: user.id,  // Auto-fill with logged-in user
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  // Insert - use destructuring and NO .single()!
  const { data, error } = await supabase
    .from('problems')
    .insert([newProblemData])
    .select();

  if (error) {
    console.error('Insert error:', error);
    throw error;
  }

  return data[0]; // Return first item from array
};

export const updateProblem = async (id, problemData) => {
  // Get logged-in user
  const { data: { user } } = await supabase.auth.getUser();

  const updatedData = {
    ...problemData,
    last_updated_by: user?.id || null,
    updated_at: new Date().toISOString()
  };

  const { data, error } = await supabase
    .from('problems')
    .update(updatedData)
    .eq('id', id)
    .select('*, updater:users!fk_task_updater(name)');

  if (error) {
    console.error('Update error:', error);
    throw error;
  }

  return data[0]; // Return first item from array
};

export const deleteProblem = async (id) => {
  const { error } = await supabase
    .from('problems')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Delete error:', error);
    throw error;
  }

  // Return success indicator
  return { success: true };
};

// SUBTASKS
/**
 * Fetch all subtasks for a specific problem
 * @param {string} problemId - The problem ID
 * @returns {Array} Array of subtask objects
 */
export const fetchSubtasks = async (problemId) => {
  const { data, error } = await supabase
    .from('subtasks')
    .select('*')
    .eq('problem_id', problemId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Fetch subtasks error:', error);
    throw error;
  }

  return data || [];
};

/**
 * Save subtasks for a problem (update existing, insert new, delete removed)
 * @param {string} problemId - The problem ID
 * @param {Array} subtasks - Array of subtask objects
 * @returns {Object} Result with data and error
 */
export const saveSubtasks = async (problemId, subtasks) => {
  console.log('saveSubtasks: Starting for problem:', problemId);
  console.log('Subtasks to save:', subtasks);

  if (!problemId) {
    const error = new Error('Problem ID is required to save subtasks');
    console.error('saveSubtasks: Missing problem ID');
    throw error;
  }

  // Fetch existing subtasks to compare
  const { data: existingSubtasks, error: fetchError } = await supabase
    .from('subtasks')
    .select('*')
    .eq('problem_id', problemId);

  if (fetchError) {
    console.error('Failed to fetch existing subtasks:', fetchError.message);
    throw new Error('Failed to fetch existing subtasks.');
  }

  console.log('Found', existingSubtasks?.length || 0, 'existing subtasks');

  // Handle empty subtasks array - delete all existing
  if (!subtasks || subtasks.length === 0) {
    if (existingSubtasks && existingSubtasks.length > 0) {
      console.log('Deleting all subtasks for problem:', problemId);
      const { error: deleteError } = await supabase
        .from('subtasks')
        .delete()
        .eq('problem_id', problemId);

      if (deleteError) {
        console.error('Failed to delete subtasks:', deleteError.message);
        throw new Error('Failed to delete subtasks.');
      }
    }
    console.log('No subtasks to save for problem', problemId);
    return { data: [], error: null };
  }

  // Filter valid subtasks
  const validSubtasks = subtasks.filter(st => st && st.title && st.title.trim() !== '');

  if (validSubtasks.length === 0) {
    console.log('No valid subtasks to save for problem', problemId);
    return { data: [], error: null };
  }

  // Separate subtasks into: update (has id), insert (no id), and delete (existing not in new list)
  const subtasksToUpdate = validSubtasks.filter(st => st.id);
  const subtasksToInsert = validSubtasks.filter(st => !st.id);
  const existingIds = new Set((existingSubtasks || []).map(st => st.id));
  const newIds = new Set(subtasksToUpdate.map(st => st.id));
  const idsToDelete = [...existingIds].filter(id => !newIds.has(id));

  console.log('Update:', subtasksToUpdate.length, 'Insert:', subtasksToInsert.length, 'Delete:', idsToDelete.length);

  // Delete removed subtasks
  if (idsToDelete.length > 0) {
    const { error: deleteError } = await supabase
      .from('subtasks')
      .delete()
      .in('id', idsToDelete);

    if (deleteError) {
      console.error('Failed to delete removed subtasks:', deleteError.message);
      throw new Error('Failed to delete removed subtasks.');
    }
    console.log('Deleted', idsToDelete.length, 'subtasks');
  }

  // Update existing subtasks (only if changed)
  const updatedSubtasks = [];
  for (const st of subtasksToUpdate) {
    // Find the existing subtask to compare
    const existing = existingSubtasks.find(e => e.id === st.id);
    
    // Check if anything actually changed
    const hasChanges = !existing || 
      existing.title !== st.title.trim() ||
      existing.due_date !== (st.due_date || null) ||
      existing.is_completed !== (st.is_completed || false) ||
      existing.solution !== (st.solution || null) ||
      existing.assigned_to !== (st.assigned_to || null) ||
      existing.completed_date !== (st.completed_date || null);
    
    if (!hasChanges) {
      console.log(`⏭️ Skipping subtask ${st.id} (no changes)`);
      updatedSubtasks.push(existing);
      continue;
    }
    
    console.log(`📝 Updating subtask ${st.id} (has changes)`);
    const { data, error: updateError } = await supabase
      .from('subtasks')
      .update({
        title: st.title.trim(),
        due_date: st.due_date || null,
        is_completed: st.is_completed || false,
        solution: st.solution || null,
        assigned_to: st.assigned_to || null,
        completed_date: st.completed_date || null
      })
      .eq('id', st.id)
      .select('*')
      .single();

    if (updateError) {
      console.error('Failed to update subtask:', st.id, updateError.message);
      throw new Error('Failed to update subtask.');
    }
    updatedSubtasks.push(data);
  }
  console.log('Updated', updatedSubtasks.filter((_, i) => {
    const st = subtasksToUpdate[i];
    const existing = existingSubtasks.find(e => e.id === st.id);
    return !existing || existing.title !== st.title.trim() || existing.solution !== (st.solution || null);
  }).length, 'subtasks (skipped', subtasksToUpdate.length - updatedSubtasks.filter((_, i) => {
    const st = subtasksToUpdate[i];
    const existing = existingSubtasks.find(e => e.id === st.id);
    return !existing || existing.title !== st.title.trim() || existing.solution !== (st.solution || null);
  }).length, 'unchanged)');

  // Insert new subtasks
  const insertedSubtasks = [];
  if (subtasksToInsert.length > 0) {
    const subtasksData = subtasksToInsert.map(st => ({
      problem_id: problemId,
      title: st.title.trim(),
      due_date: st.due_date || null,
      is_completed: st.is_completed || false,
      solution: st.solution || null,
      assigned_to: st.assigned_to || null,
      completed_date: st.completed_date || null
    }));

    const { data, error: insertError } = await supabase
      .from('subtasks')
      .insert(subtasksData)
      .select('*');

    if (insertError) {
      console.error('Failed to insert new subtasks:', insertError.message);
      throw new Error('Failed to insert new subtasks.');
    }
    insertedSubtasks.push(...(data || []));
    console.log('Inserted', insertedSubtasks.length, 'subtasks');
  }

  const allSubtasks = [...updatedSubtasks, ...insertedSubtasks];
  console.log('Successfully saved', allSubtasks.length, 'subtasks');
  return { data: allSubtasks, error: null };
};

/**
 * Update a single subtask's completion status
 * @param {string} subtaskId - The subtask ID
 * @param {boolean} isCompleted - Completion status
 * @returns {Object} Updated subtask
 */
export const updateSubtaskStatus = async (subtaskId, isCompleted) => {
  const { data, error } = await supabase
    .from('subtasks')
    .update({ is_completed: isCompleted })
    .eq('id', subtaskId)
    .select()
    .single();

  if (error) {
    console.error('Update subtask status error:', error);
    throw error;
  }

  return data;
};

// SOLUTIONS
// Helper function to create the delete function in Supabase
export const createDeleteFunction = async () => {
  try {
    console.log('🔧 Creating delete_solutions_for_problem function...');

    const functionSQL = `
      CREATE OR REPLACE FUNCTION delete_solutions_for_problem(p_problem_id UUID)
      RETURNS INTEGER
      LANGUAGE plpgsql
      SECURITY DEFINER
      AS $$
      DECLARE
          deleted_count INTEGER;
      BEGIN
          DELETE FROM solutions 
          WHERE problem_id = p_problem_id;
          
          GET DIAGNOSTICS deleted_count = ROW_COUNT;
          
          RETURN deleted_count;
      END;
      $$;
      
      GRANT EXECUTE ON FUNCTION delete_solutions_for_problem(UUID) TO authenticated;
    `;

    const { data, error } = await supabase.rpc('exec_sql', { sql: functionSQL });

    if (error) {
      console.error('❌ Error creating function:', error);
      throw error;
    }

    console.log('✅ Function created successfully');
    return data;
  } catch (error) {
    console.error('❌ Failed to create function:', error);
    console.log('ℹ️ Please run the SQL commands in debug-rls-policies.sql manually in Supabase SQL Editor');
    throw error;
  }
};

// Helper function to debug RLS policies
export const debugSolutionsRLS = async () => {
  try {
    console.log('🔍 Debugging RLS policies for solutions table...');

    // Try to get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) {
      console.error('❌ Error getting current user:', userError);
      return;
    }

    console.log('👤 Current user:', user?.id);

    // Try a simple select to test read permissions
    const { data: testSelect, error: selectError } = await supabase
      .from('solutions')
      .select('*')
      .limit(1);

    if (selectError) {
      console.error('❌ SELECT test failed:', selectError);
    } else {
      console.log('✅ SELECT test passed, found', testSelect?.length || 0, 'solutions');
    }

    // Try a test delete on non-existent record to see if delete is allowed
    const { data: testDelete, error: deleteError } = await supabase
      .from('solutions')
      .delete()
      .eq('problem_id', 'test-non-existent-id')
      .select();

    if (deleteError) {
      console.error('❌ DELETE test failed:', deleteError);
      if (deleteError.code === '42501') {
        console.error('🚨 RLS POLICY ISSUE: DELETE operations are blocked by Row Level Security');
      }
    } else {
      console.log('✅ DELETE test passed (no records to delete, but permission granted)');
    }

  } catch (error) {
    console.error('❌ RLS debug error:', error);
  }
};

// Create or update solutions for a problem
export const saveSolutions = async (problemId, solutions) => {
  console.log('💾 saveSolutions: Starting for problem:', problemId);
  console.log('💾 Solutions to save:', solutions);

  if (!problemId) {
    const error = new Error('Problem ID is required to save solutions');
    console.error('❌ saveSolutions: Missing problem ID');
    throw error;
  }

  // 1. **Delete Existing Solutions** (RE-ENABLE THIS STEP!)
  // --- THIS IS THE CRITICAL LINE ---
  console.log('🗑️ Deleting existing solutions for problem:', problemId);
  const { error: deleteError } = await supabase
    .from('solutions')
    .delete()
    .eq('problem_id', problemId);

  if (deleteError) {
    console.error("Failed to delete old solutions:", deleteError.message);
    console.error("Delete error details:", deleteError);
    // We must return here, otherwise we insert duplicates!
    throw new Error("Deletion failed. Cannot save solutions.");
  }

  console.log('✅ Successfully deleted existing solutions');

  // 2. Prepare new solutions for insertion
  if (!solutions || solutions.length === 0) {
    console.log(`No solutions to insert for problem ${problemId}. Clean slate saved.`);
    return { data: [], error: null };
  }

  const solutionsToInsert = solutions
    .filter(s => s && s.description && s.description.trim() !== '')
    .map(s => ({
      problem_id: problemId,
      description: s.description.trim(),
      is_chosen: s.is_chosen || false
      // Note: We exclude the 'id' field to ensure Supabase generates fresh IDs
    }));

  if (solutionsToInsert.length === 0) {
    console.log(`No valid solutions to insert for problem ${problemId}. Clean slate saved.`);
    return { data: [], error: null };
  }

  console.log('💾 Inserting', solutionsToInsert.length, 'solutions');

  // 3. Insert the new/updated solutions list
  const { data: insertedSolutions, error: insertError } = await supabase
    .from('solutions')
    .insert(solutionsToInsert)
    .select('*');

  if (insertError) {
    console.error("Failed to insert new solutions:", insertError.message);
    console.error("Insert error details:", insertError);
    throw new Error("Insertion failed. Solution update incomplete.");
  }

  console.log('✅ Successfully inserted', insertedSolutions?.length || 0, 'solutions');
  return { data: insertedSolutions, error: null };
};

// Fetch solutions for a problem
export const fetchSolutions = async (problemId) => {
  const { data, error } = await supabase
    .from('solutions')
    .select('*')
    .eq('problem_id', problemId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Fetch solutions error:', error);
    throw error;
  }

  return data || [];
};

// DOCUMENTS
export const fetchDocuments = async () => {
  const { data, error } = await supabase
    .from('documents')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const uploadDocument = async (file, metadata) => {
  console.log('📤 uploadDocument: Starting upload for file:', file.name);
  console.log('📤 uploadDocument: File size:', file.size, 'bytes');
  console.log('📤 uploadDocument: File type:', file.type);
  console.log('📤 uploadDocument: Metadata:', metadata);

  try {
    // Get logged-in user
    const { data: { user }, error: userError } = await supabase.auth.getUser();

    if (userError) {
      console.error('❌ Error getting user:', userError);
      throw new Error('Authentication required for file upload');
    }

    if (!user) {
      throw new Error('User not authenticated');
    }

    console.log('👤 uploadDocument: User ID:', user.id);

    // Define filePath using user ID and a unique timestamp/filename combination
    const sanitizedFileName = file.name.replace(/\s/g, '_');
    const filePath = `${user.id}/${Date.now()}_${sanitizedFileName}`;

    console.log('📁 uploadDocument: File path:', filePath);

    // UPLOAD LOGIC: Ensure the logic relies ONLY on the destructured response
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('documents')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      // Handle error and RETURN immediately. Avoid manual response parsing.
      console.error("Storage Error:", uploadError.message);
      console.error("Storage Error Details:", uploadError);
      return { success: false, error: uploadError.message };
    }

    console.log('✅ uploadDocument: File uploaded successfully:', uploadData);

    // Get public URL (this should not cause response body issues)
    const { data: urlData } = supabase.storage
      .from('documents')
      .getPublicUrl(filePath);

    const publicUrl = urlData.publicUrl;
    console.log('🔗 uploadDocument: Public URL generated:', publicUrl);

    // METADATA INSERTION: Insert the document record into the 'documents' PostgreSQL table
    const documentRecord = {
      filename: file.name,
      file_path: filePath,
      file_type: file.type,
      file_size: file.size,
      file_url: publicUrl,
      category: metadata.category,
      description: metadata.description || '',
      uploaded_by: user.id,
      created_at: new Date().toISOString()
    };

    console.log('💾 uploadDocument: Inserting document record:', documentRecord);

    const { data: insertedData, error: insertError } = await supabase
      .from('documents')
      .insert([documentRecord])
      .select();

    if (insertError) {
      console.error("Failed to insert document metadata:", insertError.message);
      console.error("Insert error details:", insertError);

      // Clean up the uploaded file since metadata insertion failed
      try {
        await supabase.storage.from('documents').remove([filePath]);
        console.log('🧹 uploadDocument: Cleaned up uploaded file after metadata failure');
      } catch (cleanupError) {
        console.error('❌ Failed to cleanup file after metadata error:', cleanupError);
      }

      return { success: false, error: insertError.message };
    }

    console.log('✅ uploadDocument: Document metadata inserted successfully:', insertedData);
    
    // Note: Automatic RAG processing disabled to prevent quota exhaustion
    // Documents will be processed manually or on-demand
    console.log('ℹ️  Document uploaded successfully. RAG processing can be triggered manually if needed.');

    // Return success
    return {
      success: true,
      data: insertedData[0],
      error: null
    };

  } catch (error) {
    console.error('❌ uploadDocument: Unexpected error:', error);
    return {
      success: false,
      error: error.message || 'Upload failed'
    };
  }
};

export const downloadDocument = async (filePath, filename) => {
  const { data, error } = await supabase.storage
    .from('documents')
    .download(filePath);

  if (error) throw error;

  // Create download link
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const deleteDocument = async (id, filePath) => {
  // Delete from storage
  const { error: storageError } = await supabase.storage
    .from('documents')
    .remove([filePath]);

  if (storageError) {
    console.error('Storage delete error:', storageError);
    throw storageError;
  }

  // Delete from database
  const { error } = await supabase
    .from('documents')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Delete error:', error);
    throw error;
  }

  // Return success indicator
  return { success: true };
};

